// Audit log (admin only): TanStack Table + Virtual so thousands of entries
// scroll smoothly; filters live in the URL. Entries are append-only and
// written by the Functions, never by the client.
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { AlertTriangle, ScrollText, Search } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import {
  categoria,
  categorias,
  describir,
  esCritica,
  textoBuscable,
  type Categoria,
} from '@/auditoria/descripcion';
import { useAuditoria, useDispositivos, useEquipos, useUsuarios } from '@/datos/consultas';
import { aFecha, type AuditoriaDoc } from '@/datos/tipos';
import { exigir } from '@/lib/guardias';
import { useMedio } from '@/lib/medios';
import { permisos } from '@/lib/sesion';
import { fechaCompleta, fechaHora } from '@/lib/tiempo';
import { Encabezado } from '@/shell/Encabezado';
import { ErrorCarga, Esqueleto, Vacio } from '@/ui/basicos';
import { Boton } from '@/ui/boton';
import { cn } from '@/ui/cn';

const PASO = 500;

export const Route = createFileRoute('/_app/admin/auditoria')({
  beforeLoad: exigir(permisos.administrar),
  validateSearch: z.object({
    cat: z
      .enum(['acceso', 'usuarios', 'tablets', 'tablero', 'evidencia', 'config'])
      .optional()
      .catch(undefined),
    criticas: z.boolean().optional().catch(undefined),
    q: z.string().max(80).optional().catch(undefined),
  }),
  component: Pagina,
});

const col = createColumnHelper<AuditoriaDoc>();

function Pagina() {
  const { cat, criticas, q } = Route.useSearch();
  const navegar = useNavigate({ from: Route.fullPath });
  const [limite, setLimite] = useState(PASO);
  const consulta = useAuditoria(limite);
  const usuariosQ = useUsuarios();
  const equiposQ = useEquipos();
  const movil = !useMedio('(min-width: 768px)');
  const [texto, setTexto] = useState(q ?? '');

  const nombres = useMemo(
    () =>
      new Map([...(usuariosQ.data ?? []), ...(equiposQ.data ?? [])].map((u) => [u.id, u.nombre])),
    [usuariosQ.data, equiposQ.data],
  );
  const nombre = useMemo(() => (uid: string) => nombres.get(uid), [nombres]);
  const dispositivosQ = useDispositivos();
  const tablet = useMemo(() => {
    const m = new Map((dispositivosQ.data ?? []).map((d) => [d.id, d.nombre]));
    return (id: string) => m.get(id);
  }, [dispositivosQ.data]);
  const filas = useMemo(() => {
    const t = (q ?? '').trim().toLowerCase();
    return (consulta.data ?? []).filter(
      (e) =>
        (!cat || categoria(e.accion) === cat) &&
        (!criticas || esCritica(e.accion)) &&
        (!t || textoBuscable(e, nombre, tablet).includes(t)),
    );
  }, [consulta.data, cat, criticas, q, nombre, tablet]);

  const fijar = (c: {
    cat?: Categoria | undefined;
    criticas?: boolean | undefined;
    q?: string | undefined;
  }) => void navegar({ search: (p) => ({ ...p, ...c }), replace: true });
  const total = consulta.data?.length ?? 0;
  const hayMas = total >= limite;
  const nCriticas = (consulta.data ?? []).filter((e) => esCritica(e.accion)).length;

  return (
    <div className="flex h-[calc(100dvh-78px-env(safe-area-inset-bottom))] flex-col lg:h-dvh">
      <Encabezado
        titulo="Auditoría"
        ayuda="auditoria"
        subtitulo={
          consulta.data
            ? `${filas.length === total ? total : `${filas.length} de ${total}`} registros${hayMas ? ' recientes' : ''}`
            : 'Cargando…'
        }
        buscador={false}
      />
      <div className="sin-scrollbar flex flex-none items-center gap-2 overflow-x-auto border-b border-border bg-surface px-4 py-3 *:flex-none sm:flex-wrap sm:px-7">
        <label className="relative">
          <span className="sr-only">Buscar en la auditoría</span>
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-text-2" />
          <input
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              fijar({ q: e.target.value.trim() || undefined });
            }}
            placeholder="Persona, patente, tablet…"
            className="h-[38px] w-[220px] rounded-[10px] border border-border-input bg-surface pr-3 pl-9 text-[14px] focus:border-primary focus:shadow-[0_0_0_1px_var(--tm-primary),0_0_0_4px_var(--tm-primary-soft)] focus:outline-none"
          />
        </label>
        <button
          type="button"
          aria-pressed={!!criticas}
          onClick={() => fijar({ criticas: criticas ? undefined : true })}
          className={cn(
            'inline-flex h-[38px] items-center gap-1.5 rounded-[10px] border px-3 text-[14px] transition-colors',
            criticas
              ? 'border-que-strong bg-que-soft font-bold text-que-texto'
              : 'border-border-input bg-surface text-text hover:bg-surface-muted',
          )}
        >
          <AlertTriangle className="size-4" /> Para revisar
          {nCriticas > 0 && <span className="tabular text-[12px]">{nCriticas}</span>}
        </button>
        <span className="mx-1 hidden h-6 w-px bg-border sm:block" aria-hidden="true" />
        {categorias.map((c) => (
          <button
            key={c.id}
            type="button"
            aria-pressed={cat === c.id}
            onClick={() => fijar({ cat: cat === c.id ? undefined : c.id })}
            className={cn(
              'h-[38px] rounded-full border px-3.5 text-[14px] transition-colors',
              cat === c.id
                ? 'border-primary bg-primary-soft font-bold text-primary-text'
                : 'border-border-input text-text-long hover:bg-surface-muted',
            )}
          >
            {c.texto}
          </button>
        ))}
      </div>

      {consulta.isError ? (
        <div className="p-6">
          <ErrorCarga error={consulta.error} />
        </div>
      ) : !consulta.data ? (
        <div className="flex flex-col px-7 pt-4" aria-busy="true">
          {Array.from({ length: 10 }, (_, i) => (
            <Esqueleto key={i} className="my-2 h-9" style={{ opacity: 1 - i * 0.08 }} />
          ))}
        </div>
      ) : filas.length === 0 ? (
        <Vacio
          icono={<ScrollText />}
          titulo={total ? 'Ningún registro coincide con los filtros' : 'Aún no hay registros'}
          accion={
            total ? (
              <Boton
                onClick={() => {
                  setTexto('');
                  void navegar({ search: {}, replace: true });
                }}
              >
                Quitar filtros
              </Boton>
            ) : undefined
          }
        >
          {total
            ? undefined
            : 'Aquí aparece cada ingreso, cambio de usuario, movimiento del tablero y verificación de fotos.'}
        </Vacio>
      ) : movil ? (
        <ListaMovil filas={filas} nombre={nombre} tablet={tablet} />
      ) : (
        <Tabla filas={filas} nombre={nombre} tablet={tablet} />
      )}
      {hayMas && consulta.data && (
        <div className="flex flex-none items-center justify-center gap-3 border-t border-border bg-surface px-4 py-2.5 text-[13px] text-text-2">
          Mostrando los {total} registros más recientes.
          <Boton tamano="sm" onClick={() => setLimite((n) => n + PASO)}>
            Cargar {PASO} más
          </Boton>
        </div>
      )}
    </div>
  );
}

function Quien({ e, nombre }: { e: AuditoriaDoc; nombre: (u: string) => string | undefined }) {
  if (!e.por) return <span className="text-text-2">Sistema</span>;
  return <span className="block truncate">{e.porNombre ?? nombre(e.por) ?? e.por}</span>;
}

function Enlace({ e }: { e: AuditoriaDoc }) {
  const id = e.detalle?.['inspeccionId'];
  if (typeof id !== 'string') return null;
  return (
    <Link
      to="/inspecciones/$inspeccionId"
      params={{ inspeccionId: id }}
      className="relative z-[1] text-[13px] font-bold text-primary-text hover:underline"
    >
      Ver inspección
    </Link>
  );
}

function Tabla({
  filas,
  nombre,
  tablet,
}: {
  filas: AuditoriaDoc[];
  nombre: (u: string) => string | undefined;
  tablet: (id: string) => string | undefined;
}) {
  const contenedor = useRef<HTMLDivElement>(null);
  const columnas = useMemo(
    () => [
      col.accessor((e) => aFecha(e.en)?.getTime() ?? 0, {
        id: 'fecha',
        header: 'Fecha',
        cell: (c) => (
          <span
            className="tabular whitespace-nowrap"
            title={c.getValue() ? fechaCompleta(new Date(c.getValue())) : ''}
          >
            {c.getValue() ? fechaHora(new Date(c.getValue())) : '—'}
          </span>
        ),
      }),
      col.display({
        id: 'quien',
        header: 'Quién',
        cell: (c) => <Quien e={c.row.original} nombre={nombre} />,
      }),
      col.display({
        id: 'que',
        header: 'Qué pasó',
        cell: ({ row: { original: e } }) => {
          const d = describir(e, nombre, tablet);
          const critica = esCritica(e.accion);
          return (
            <span className="flex min-w-0 items-center gap-2">
              {critica && (
                <AlertTriangle
                  className="size-4 flex-none text-que-strong"
                  aria-label="Para revisar"
                />
              )}
              <span className={cn('truncate', critica && 'font-bold text-que-texto')}>
                {d.titulo}
              </span>
            </span>
          );
        },
      }),
      col.display({
        id: 'detalle',
        header: 'Detalle',
        cell: ({ row: { original: e } }) => (
          <span className="flex min-w-0 items-center gap-3">
            <span className="truncate text-text-2">{describir(e, nombre, tablet).detalle}</span>
            <Enlace e={e} />
          </span>
        ),
      }),
    ],
    [nombre, tablet],
  );
  const tabla = useReactTable({
    data: filas,
    columns: columnas,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (e) => e.id,
  });
  const filasTabla = tabla.getRowModel().rows;
  const virtual = useVirtualizer({
    count: filasTabla.length,
    getScrollElement: () => contenedor.current,
    estimateSize: () => 49,
    overscan: 16,
  });
  const items = virtual.getVirtualItems();
  const arriba = items[0]?.start ?? 0;
  const abajo = virtual.getTotalSize() - (items[items.length - 1]?.end ?? 0);
  const anchos = ['w-[130px]', 'w-[230px]', 'w-auto', 'w-[36%]'];
  return (
    <div ref={contenedor} className="scroll-fino min-h-0 flex-1 overflow-auto" tabIndex={-1}>
      <table className="w-full min-w-[860px] table-fixed border-separate border-spacing-0 text-[14px]">
        <caption className="sr-only">
          Registro de auditoría, del más reciente al más antiguo
        </caption>
        <thead className="sticky top-0 z-10 bg-surface">
          {tabla.getHeaderGroups().map((g) => (
            <tr key={g.id}>
              {g.headers.map((h, n) => (
                <th
                  key={h.id}
                  scope="col"
                  className={cn(
                    'h-11 border-b border-text/80 px-3 text-left text-[12px] font-bold tracking-[0.06em] whitespace-nowrap text-text-2 uppercase first:pl-7',
                    anchos[n],
                  )}
                >
                  {flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {arriba > 0 && (
            <tr aria-hidden="true">
              <td style={{ height: arriba }} colSpan={columnas.length} />
            </tr>
          )}
          {items.map((v) => {
            const fila = filasTabla[v.index];
            if (!fila) return null;
            return (
              <tr
                key={fila.id}
                data-index={v.index}
                ref={virtual.measureElement}
                className="hover:bg-surface-muted/60"
              >
                {fila.getVisibleCells().map((c) => (
                  <td
                    key={c.id}
                    className="h-[49px] max-w-0 border-b border-border px-3 first:pl-7"
                  >
                    {flexRender(c.column.columnDef.cell, c.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
          {abajo > 0 && (
            <tr aria-hidden="true">
              <td style={{ height: abajo }} colSpan={columnas.length} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ListaMovil({
  filas,
  nombre,
  tablet,
}: {
  filas: AuditoriaDoc[];
  nombre: (u: string) => string | undefined;
  tablet: (id: string) => string | undefined;
}) {
  const contenedor = useRef<HTMLDivElement>(null);
  const virtual = useVirtualizer({
    count: filas.length,
    getScrollElement: () => contenedor.current,
    estimateSize: () => 86,
    overscan: 8,
  });
  return (
    <div ref={contenedor} className="min-h-0 flex-1 overflow-auto px-4 py-3">
      <ul className="relative" style={{ height: virtual.getTotalSize() }}>
        {virtual.getVirtualItems().map((v) => {
          const e = filas[v.index];
          if (!e) return null;
          const d = describir(e, nombre, tablet);
          const critica = esCritica(e.accion);
          const f = aFecha(e.en);
          return (
            <li
              key={e.id}
              data-index={v.index}
              ref={virtual.measureElement}
              className="absolute inset-x-0 pb-2"
              style={{ transform: `translateY(${v.start}px)` }}
            >
              <div
                className={cn(
                  'rounded-[14px] border bg-surface px-3.5 py-3',
                  critica ? 'border-que-strong/50' : 'border-border',
                )}
              >
                <p className={cn('text-[14px] font-bold', critica && 'text-que-texto')}>
                  {d.titulo}
                </p>
                {d.detalle && <p className="truncate text-[13px] text-text-2">{d.detalle}</p>}
                <p className="mt-1 flex gap-2 text-[12.5px] text-text-2">
                  <span className="tabular">{f ? fechaHora(f) : '—'}</span>·
                  <Quien e={e} nombre={nombre} />
                  <span className="ml-auto">
                    <Enlace e={e} />
                  </span>
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
